import { useState, useMemo, useCallback, Fragment } from 'react';
import { Text, NumberInput, DatePicker, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import { FilterItemRadio, FilterRadioContainer } from '../GenericSubElement/FilterItemRadio';
import type { RadioDateFilterValueType } from './GenericRadioDateType';
import { RadioDateFilterOperation } from './GenericRadioDateType';

const InputAndErrorContainer = styled.div`
  margin-left: ${tokens.spacer.x3};
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x0};
  align-items: center;
  flex-wrap: wrap;
`;

const ErrorContainer = styled.div`
  margin-top: ${tokens.spacer.x0};
`;

const NoWrapText = styled(Text)`
  white-space: nowrap;
`;

enum ValidationErrorEnum {
  SELECT_ONE_OPTION = 'Select an option',
  NUMBER_VALUE_IS_EMPTY = 'Please add a value',
  DATE_VALUE_IS_EMPTY = 'Please add a value ', // <= Hackish: I add a space to distinguish the 2
  NUMBER_VALUE_IS_NOT_A_NUMBER = 'Value is not a number',
  NUMBER_VALUE_IS_NEGATIVE = 'Value should be positive',
}

export const readableRadioDateOperations: Record<RadioDateFilterOperation, string> = {
  [RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO]: 'is exactly',
  [RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO]: 'is more than',
  [RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO]: 'is less than',
  [RadioDateFilterOperation.IS_ON]: 'is on',
  [RadioDateFilterOperation.IS_AFTER]: 'is after',
  [RadioDateFilterOperation.IS_BEFORE]: 'is before',
  [RadioDateFilterOperation.IS_UNKNOWN]: 'is unknown',
  [RadioDateFilterOperation.HAS_ANY_VALUE]: 'has any value',
};

interface GenericRadioDropdownProps<T> extends AbstractSingleFilterDropdownProps<T> {
  options: RadioDateFilterOperation[];
  placeholders?: Partial<Record<RadioDateFilterOperation, string>>;
}

function getFilterValueErrors(
  operation?: RadioDateFilterOperation,
  stringifyValue?: string,
  dateValue?: Date,
): ValidationErrorEnum | undefined {
  if (!operation) {
    return ValidationErrorEnum.SELECT_ONE_OPTION;
  }
  if (
    operation === RadioDateFilterOperation.IS_UNKNOWN ||
    operation === RadioDateFilterOperation.HAS_ANY_VALUE
  ) {
    return undefined;
  }
  if (
    operation === RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO ||
    operation === RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO ||
    operation === RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO
  ) {
    if (stringifyValue == null || stringifyValue === '') {
      return ValidationErrorEnum.NUMBER_VALUE_IS_EMPTY;
    }
    if (stringifyValue && Number.isNaN(parseInt(stringifyValue, 10))) {
      return ValidationErrorEnum.NUMBER_VALUE_IS_NOT_A_NUMBER;
    }
    if (stringifyValue && parseInt(stringifyValue, 10) < 0) {
      return ValidationErrorEnum.NUMBER_VALUE_IS_NEGATIVE;
    }
  }
  if (
    (operation === RadioDateFilterOperation.IS_AFTER ||
      operation === RadioDateFilterOperation.IS_BEFORE ||
      operation === RadioDateFilterOperation.IS_ON) &&
    !dateValue
  ) {
    return ValidationErrorEnum.DATE_VALUE_IS_EMPTY;
  }
  return undefined;
}

export function GenericRadioDateDropdown({
  currentValue,
  optionalDefaultValue,
  onApply,
  options = [],
  filterName,
  placeholders,
}: GenericRadioDropdownProps<RadioDateFilterValueType>): JSX.Element {
  const initialValue = useMemo(
    () => currentValue ?? optionalDefaultValue,
    [currentValue, optionalDefaultValue],
  );
  const [dirtyOperation, setDirtyOperation] = useState<RadioDateFilterOperation | undefined>(
    initialValue?.operation ?? options.find(() => true),
  );
  // We handle the value as string to be able to check for empty string and correctness errors
  const [dirtyNumberValue, setDirtyNumberValue] = useState<string | undefined>(
    RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO === initialValue?.operation ||
      RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO === initialValue?.operation ||
      RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO === initialValue?.operation
      ? `${initialValue.value}`
      : undefined,
  );
  const [dirtyDateValue, setDirtyDateValue] = useState<Date | undefined>(
    RadioDateFilterOperation.IS_AFTER === initialValue?.operation ||
      RadioDateFilterOperation.IS_BEFORE === initialValue?.operation ||
      RadioDateFilterOperation.IS_ON === initialValue?.operation
      ? initialValue.value
      : undefined,
  );

  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const onOperationChange = useCallback(
    (newOperation: RadioDateFilterOperation) => {
      setDirtyOperation(newOperation);
      setValidationError(undefined);
    },
    [setDirtyOperation, setValidationError],
  );

  const onFilterValueChange = useCallback(
    (newValue: string) => {
      setDirtyNumberValue(newValue);
      setValidationError(undefined);
    },
    [setDirtyNumberValue, setValidationError],
  );

  const onFilterDateChange = useCallback(
    (newDate?: Date) => {
      setDirtyDateValue(newDate);
      setValidationError(undefined);
    },
    [setDirtyDateValue, setValidationError],
  );

  const handleApply = useCallback(() => {
    const trimmedDirtyValue = dirtyNumberValue?.trim();
    const filterValueErrors = getFilterValueErrors(
      dirtyOperation,
      trimmedDirtyValue,
      dirtyDateValue,
    );
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
      setDirtyNumberValue(trimmedDirtyValue);
    } else if (!dirtyOperation) {
      onApply(undefined);
    } else if (
      dirtyOperation === RadioDateFilterOperation.IS_UNKNOWN ||
      dirtyOperation === RadioDateFilterOperation.HAS_ANY_VALUE
    ) {
      onApply({
        operation: dirtyOperation,
      });
    } else if (
      trimmedDirtyValue != null &&
      (RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO === dirtyOperation ||
        RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO === dirtyOperation ||
        RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO === dirtyOperation)
    ) {
      onApply({
        operation: dirtyOperation,
        value: parseInt(trimmedDirtyValue, 10),
      });
    } else if (
      dirtyDateValue &&
      (RadioDateFilterOperation.IS_ON === dirtyOperation ||
        RadioDateFilterOperation.IS_AFTER === dirtyOperation ||
        RadioDateFilterOperation.IS_BEFORE === dirtyOperation)
    ) {
      onApply({
        operation: dirtyOperation,
        value: dirtyDateValue,
      });
    }
  }, [onApply, dirtyOperation, dirtyNumberValue, dirtyDateValue]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} onApply={handleApply}>
      <GenericFilterBody>
        <FilterRadioContainer>
          {options.map((option) => {
            return (
              <Fragment key={option}>
                <FilterItemRadio
                  isChecked={option === dirtyOperation}
                  id={`${filterName}-dropdown-${option}`}
                  name={option}
                  onChange={() => onOperationChange(option)}
                  value={`${option}`}
                  labelContent={readableRadioDateOperations[option]}
                  isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
                />
                {dirtyOperation === option &&
                  [
                    RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO,
                    RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
                    RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
                  ].includes(dirtyOperation) && (
                    <InputAndErrorContainer>
                      <InputContainer>
                        <NumberInput
                          placeholder={placeholders ? placeholders[option] : ''}
                          value={dirtyNumberValue}
                          onChange={(e) => onFilterValueChange(e.target.value)}
                          isInvalid={
                            validationError === ValidationErrorEnum.NUMBER_VALUE_IS_EMPTY ||
                            validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NOT_A_NUMBER ||
                            validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NEGATIVE
                          }
                          size="s"
                          role="textbox"
                        />
                        <NoWrapText>days ago</NoWrapText>
                      </InputContainer>
                      {(validationError === ValidationErrorEnum.NUMBER_VALUE_IS_EMPTY ||
                        validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NOT_A_NUMBER ||
                        validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NEGATIVE) && (
                        <ErrorContainer>
                          <Text textColor="text_error">{validationError}</Text>
                        </ErrorContainer>
                      )}
                    </InputAndErrorContainer>
                  )}
                {dirtyOperation === option &&
                  [
                    RadioDateFilterOperation.IS_AFTER,
                    RadioDateFilterOperation.IS_BEFORE,
                    RadioDateFilterOperation.IS_ON,
                  ].includes(dirtyOperation) && (
                    <InputAndErrorContainer>
                      <DatePicker
                        value={dirtyDateValue}
                        onDateChange={onFilterDateChange}
                        isInvalid={
                          validationError === ValidationErrorEnum.NUMBER_VALUE_IS_EMPTY ||
                          validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NOT_A_NUMBER
                        }
                        size="s"
                      />
                      {(validationError === ValidationErrorEnum.NUMBER_VALUE_IS_EMPTY ||
                        validationError === ValidationErrorEnum.NUMBER_VALUE_IS_NOT_A_NUMBER) && (
                        <ErrorContainer>
                          <Text textColor="text_error">{validationError}</Text>
                        </ErrorContainer>
                      )}
                    </InputAndErrorContainer>
                  )}
              </Fragment>
            );
          })}
          {validationError === ValidationErrorEnum.SELECT_ONE_OPTION && (
            <Text textColor="text_error">{validationError}</Text>
          )}
        </FilterRadioContainer>
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
